import Grid from "@mui/material/Grid2";
import { useFieldArray, useForm } from "react-hook-form";
import { useEffect, useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";

import { TabSessionStorageResponse } from "../../../models/api/TabSessionStorage";
import { defaultTabData, TabData } from "../../../models/TabSessionStorage";
import PatientInfo from "../../PatientInfoHeader/PatientInfo";
import { MemberInfoData, PatientInformation } from "../../../models/Patient";
import { currentDate, getValue } from "../../../utils";
import PriorAuthTabs from "../containers/PriorAuthTabs";
import { rules } from "../../../utils/validation/Validation";
import { Numbers } from "../../../constants/Referral";
import { MyAccountModel } from "../../../models/MyAccount";
import { formatDate } from "../../../utils/DateTime";
import { DateFormats } from "../../../constants/Date";
import { mainContentStyle, overFlowAuto } from "../styles/style";
import { EligibilityCheckActionDispatchTypes } from "../../../constants/Patient";

export interface PropsFromState {
  tabSessionStorage: TabSessionStorageResponse;
  memberInfoDataState: MemberInfoData | undefined;
  patientInformationByIdState: PatientInformation;
  loggedInUserState: MyAccountModel;
}
export interface PropsFromDispatch {
  saveTabSessionStorage: (sessionData: TabData) => void;
  resetStates: (actionType: string, field?: string) => void;
}

type AllProps = PropsFromState & PropsFromDispatch;

const AuthorizationForm: React.FC<AllProps> = ({
  saveTabSessionStorage,
  tabSessionStorage,
  memberInfoDataState,
  patientInformationByIdState,
  loggedInUserState,
  resetStates
}: AllProps) => {
  const [currentTab, setCurrentTab] = useState(0);
  const [formError, setFormError] = useState<any>();
  const [fileUpload, setFileUpload] = useState<File[]>([]);
  const [reimbursementTypeId, setReimbursementTypeId] = useState<number>(Numbers.ZERO);
  const [uuid, setUuid] = useState<string>("");
  const methods = useForm<TabData>({
    defaultValues: defaultTabData,
    mode: "all",
    criteriaMode: "all", // Ensures all validation errors are collected
  });

  const {
    reset,
    getValues,
    control,
    setValue,
    clearErrors,
    watch,
    handleSubmit,
    trigger,
    formState,
  } = methods;
  const { errors } = formState;

  useEffect(() => {
    setFormError(tabSessionStorage);
  }, [tabSessionStorage]);

  useEffect(() => {
    setUuid(uuidv4());
   }, [])

  const saveDetails = (data: any) => {
    const payloadData = {
      ...data,
      icdCodesDetails: {
        ...data.icdCodesDetails,
        icdCodesInfoDto: data.diagnosisCode,
        fullNameCreated: getValue(
          loggedInUserState,
          "providerPortalUser.email"
        ),
        typeId: 1,
      },
      requestService: {
        ...data.requestService,
        startOfCare: formatDate(
          getValue(data, "requestService.startOfCare"),
          DateFormats.YYYY_MM_DD
        ),
      },
      referralSource: {
        ...data.referralSource,
        dischargeDate: data.referralSource?.dischargeDate
          ? formatDate(data.referralSource.dischargeDate, DateFormats.YYYY_MM_DD)
          : data.referralSource?.dischargeDate,
      },
      requestId: uuid,
    };
    SubmitForm(payloadData);
  };
  const SubmitForm = (data: TabData) => {
    if (Object.keys(data).length !== 0) {
      saveTabSessionStorage(data);
    }
  };

  const {
    fields: icd10RecordsFields,
    append: icd10RecordsAppend,
    remove: icd10RecordsRemove,
  } = useFieldArray({
    control,
    name: "diagnosisCode",
    rules: useMemo(() => {
      return currentTab === 3 ? rules.icdCodes : { required: false };
    }, [currentTab]),
  });

  const values = getValues();

  useEffect(() => {
    if (
      patientInformationByIdState &&
      getValue(patientInformationByIdState, "patientId") > Numbers.ZERO
    ) {
      const dateCreated = currentDate();
      reset({
        ...getValues(),
        physicianDetails: {
          ...getValues("physicianDetails"),
          primaryPhysician: {
            physicianName: getValue(
              patientInformationByIdState,
              "primaryPhysician.physicianName"
            ),
            physicianNpiId: getValue(
              patientInformationByIdState,
              "primaryPhysician.physicianNpiId"
            ),
            streetName1: getValue(
              patientInformationByIdState,
              "primaryPhysician.streetName1"
            ),
            streetName2: getValue(
              patientInformationByIdState,
              "primaryPhysician.streetName2"
            ),
            city: getValue(
              patientInformationByIdState,
              "primaryPhysician.city"
            ),
            county: getValue(
              patientInformationByIdState,
              "primaryPhysician.county"
            ),
            physicianState: getValue(
              patientInformationByIdState,
              "primaryPhysician.physicianState"
            ),
            zipCode: getValue(
              patientInformationByIdState,
              "primaryPhysician.zipCode"
            ),
            fax: getValue(patientInformationByIdState, "primaryPhysician.fax"),
            phoneExt: getValue(
              patientInformationByIdState,
              "primaryPhysician.phoneExt"
            ),
            phoneNo: getValue(
              patientInformationByIdState,
              "primaryPhysician.phoneNo"
            ),
            physicianTypeId: 1,
            longitude: null,
            latitude: null,
          },
        },
        userCreated: getValue(loggedInUserState, "providerPortalUser.email"),
        requestId: uuid,
        referralIdCreated: dateCreated,
        noteComment: "",
        dateCreated: dateCreated
      });
    }

    return () => {
      resetStates(EligibilityCheckActionDispatchTypes.ELIGIBILITY_CHECK_RESET)
    }
  }, [patientInformationByIdState]);

  const submitDetails = () =>
  {
    handleSubmit(
      (data) => {
        saveDetails(data);
      },
      (formErrors) => {
        setFormError(formErrors);
      }
    )();
  };
  return (
    <Grid
      container
      direction="column"
      sx={mainContentStyle}
    >
      <Grid  sx={overFlowAuto}>
        <PatientInfo watch={watch} memberInfoDataState={memberInfoDataState} label={"PRIOR AUTHORIZATION FORM"} type={1} />
      <PriorAuthTabs
        setCurrentTab={setCurrentTab}
        saveTabSessionStorage={saveTabSessionStorage}
        tabSessionStorage={tabSessionStorage}
        currentTab={currentTab}
        watch={watch}
        getValues={getValues}
        reset={reset}
        setValue={setValue}
        control={control}
        clearErrors={clearErrors}
        formError={formError}
        trigger={trigger}
        values={values}
        icd10RecordsFields={icd10RecordsFields}
        icd10RecordsAppend={icd10RecordsAppend}
        icd10RecordsRemove={icd10RecordsRemove}
        formState={formState}
        handleSubmit={submitDetails}
        setFileUpload={setFileUpload}
        fileUpload={fileUpload}
        errors={errors}
        setReimbursementTypeId={setReimbursementTypeId}
        reimbursementTypeId={reimbursementTypeId}
      />
      </Grid>
    </Grid>
  );
};

export default AuthorizationForm;
